import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import {
  StatTileWidget,
  ProgressBarWidget,
  StreakCounterWidget,
  LineChartWidget,
  BarChartWidget,
  PieChartWidget,
  QuickLogWidget,
  GoalTrackerWidget,
  MilestoneTimelineWidget,
  QuickActionsWidget,
  QuoteWidget,
  NotificationWidget,
  LogCardGridWidget,
} from './widgets';
import type { WidgetInstance, DashboardConfig } from '@/types/dashboard';

// Re-export types for backwards compatibility
export type { WidgetInstance, DashboardConfig } from '@/types/dashboard';

interface DashboardRendererProps {
  config: DashboardConfig;
  userData?: Record<string, unknown>;
  onQuickLog?: (logType: string) => Promise<void>;
  className?: string;
}

// Widget component registry
const WIDGET_COMPONENTS: Record<string, React.ComponentType<{ config: Record<string, unknown>; data?: unknown; onLog?: () => Promise<void> }>> = {
  'stat-tile': StatTileWidget as React.ComponentType<{ config: Record<string, unknown>; data?: unknown }>,
  'progress-bar': ProgressBarWidget as React.ComponentType<{ config: Record<string, unknown>; data?: unknown }>,
  'streak-counter': StreakCounterWidget as React.ComponentType<{ config: Record<string, unknown>; data?: unknown }>,
  'line-chart': LineChartWidget as React.ComponentType<{ config: Record<string, unknown>; data?: unknown }>,
  'bar-chart': BarChartWidget as React.ComponentType<{ config: Record<string, unknown>; data?: unknown }>,
  'pie-chart': PieChartWidget as React.ComponentType<{ config: Record<string, unknown>; data?: unknown }>,
  'quick-log': QuickLogWidget as React.ComponentType<{ config: Record<string, unknown>; data?: unknown; onLog?: () => Promise<void> }>,
  'goal-tracker': GoalTrackerWidget as React.ComponentType<{ config: Record<string, unknown>; data?: unknown }>,
  'milestone-timeline': MilestoneTimelineWidget as React.ComponentType<{ config: Record<string, unknown>; data?: unknown }>,
  'quick-actions': QuickActionsWidget as React.ComponentType<{ config: Record<string, unknown>; data?: unknown }>,
  'quote': QuoteWidget as React.ComponentType<{ config: Record<string, unknown>; data?: unknown }>,
  'notification': NotificationWidget as React.ComponentType<{ config: Record<string, unknown>; data?: unknown }>,
  'log-card-grid': LogCardGridWidget as React.ComponentType<{ config: Record<string, unknown>; data?: unknown; onLog?: () => Promise<void> }>,
};

// Mapping from widget template slugs to expected data keys from useDashboardWidgetData
const WIDGET_DATA_MAPPING: Record<string, string[]> = {
  'stat-tile': ['cigarettesToday', 'moodRating', 'stressLevel', 'anxietyLevel', 'cravingsCount'],
  'progress-bar': ['goalProgress'],
  'streak-counter': ['currentStreak', 'longestStreak'],
  'line-chart': ['weeklyProgress'],
  'bar-chart': ['weeklyProgress'],
  'pie-chart': ['weeklyProgress'],
  'quick-log': [],
  'goal-tracker': ['goalProgress', 'targetCigarettes'],
  'milestone-timeline': ['milestonesCompleted'],
  'quick-actions': [],
  'quote': [],
  'notification': ['notifications'],
  'log-card-grid': [],
};

// Get user data for a specific widget based on its config
function getWidgetData(widget: WidgetInstance, userData?: Record<string, unknown>) {
  if (!userData) return undefined;
  
  const metricKey = widget.config.metricKey as string | undefined;
  const dataSource = widget.config.dataSource as string | undefined;
  
  // If widget specifies a data source, use it
  if (dataSource && userData[dataSource] !== undefined) {
    return userData[dataSource];
  }
  
  // If widget specifies a metric key, extract from user data
  if (metricKey && userData[metricKey] !== undefined) {
    return { value: userData[metricKey] };
  }
  
  // Use the mapping to get relevant data for this widget type
  const relevantKeys = WIDGET_DATA_MAPPING[widget.templateSlug];
  if (relevantKeys && relevantKeys.length > 0) {
    const mappedData: Record<string, unknown> = {};
    for (const key of relevantKeys) {
      if (userData[key] !== undefined) {
        mappedData[key] = userData[key];
      }
    }
    if (Object.keys(mappedData).length > 0) {
      return mappedData;
    }
  }
  
  // Return the entire userData for widgets that need multiple fields
  return userData;
}

export function DashboardRenderer({ 
  config, 
  userData,
  onQuickLog,
  className 
}: DashboardRendererProps) {
  const { widgets, layout } = config;
  const columns = layout.columns || 12;

  // Sort widgets by position for proper rendering order
  const sortedWidgets = useMemo(() => {
    return [...widgets].sort((a, b) => {
      // Sort by row first (y), then by column (x)
      if (a.position.y !== b.position.y) {
        return a.position.y - b.position.y;
      }
      return a.position.x - b.position.x;
    });
  }, [widgets]);

  // Render based on layout type
  const gridContent = useMemo(() => {
    if (layout.type === 'fixed') {
      // Fixed layout uses absolute positioning
      return (
        <div className="relative" style={{ minHeight: '600px' }}>
          {sortedWidgets.map((widget) => {
            const Component = WIDGET_COMPONENTS[widget.templateSlug];
            if (!Component) {
              console.warn(`Unknown widget type: ${widget.templateSlug}`);
              return null;
            }

            const widgetData = getWidgetData(widget, userData);
            const rowHeight = layout.rowHeight || 100;

            return (
              <div
                key={widget.id}
                className="absolute"
                style={{
                  left: `${(widget.position.x / columns) * 100}%`,
                  top: `${widget.position.y * rowHeight}px`,
                  width: `${(widget.position.w / columns) * 100}%`,
                  height: `${widget.position.h * rowHeight}px`,
                  padding: '8px',
                }}
              >
                <Component 
                  config={widget.config} 
                  data={widgetData}
                  onLog={widget.templateSlug === 'quick-log' && onQuickLog 
                    ? () => onQuickLog(widget.config.logType as string || 'default')
                    : undefined
                  }
                />
              </div>
            );
          })}
        </div>
      );
    }

    // Grid or masonry layout
    return (
      <div 
        className={cn(
          "grid gap-4",
          layout.type === 'masonry' ? 'auto-rows-min' : ''
        )}
        style={{
          gridTemplateColumns: `repeat(${Math.min(columns, 12)}, minmax(0, 1fr))`,
        }}
      >
        {sortedWidgets.map((widget) => {
          const Component = WIDGET_COMPONENTS[widget.templateSlug];
          if (!Component) {
            console.warn(`Unknown widget type: ${widget.templateSlug}`);
            return null;
          }

          const widgetData = getWidgetData(widget, userData);
          const colSpan = Math.min(widget.position.w, columns);
          const rowSpan = widget.position.h;

          return (
            <div
              key={widget.id}
              style={{
                gridColumn: `span ${colSpan}`,
                gridRow: `span ${rowSpan}`,
              }}
            >
              <Component 
                config={widget.config} 
                data={widgetData}
                onLog={widget.templateSlug === 'quick-log' && onQuickLog 
                  ? () => onQuickLog(widget.config.logType as string || 'default')
                  : undefined
                }
              />
            </div>
          );
        })}
      </div>
    );
  }, [sortedWidgets, layout, columns, userData, onQuickLog]);

  if (widgets.length === 0) {
    return (
      <div className={cn("text-center py-12 text-muted-foreground", className)}>
        <p>No widgets configured for this dashboard.</p>
      </div>
    );
  }

  return (
    <div className={className}>
      {gridContent}
    </div>
  );
}
